/**
 * Full client-side session wipe.
 *
 * The dashboard's sign-out used to remove two cookies by name (`authToken`,
 * `ins_code`) and leave the other eight the app writes — including
 * `refreshToken`, a credential that keeps minting ID tokens for the
 * signed-out account until it's revoked. Naming cookies individually also
 * meant every new cookie added anywhere in the app had to be remembered
 * here, and none of them were.
 *
 * So this sweeps everything JS can see on the current origin instead of
 * working from a list. Two caveats worth knowing:
 *
 *  - HttpOnly cookies are invisible to `document.cookie` and cannot be
 *    removed from here at all. Only the server that set them can clear them.
 *  - A cookie scoped to a deeper path than the current page isn't visible
 *    either — from "/" you cannot see, let alone delete, a "/dashboard"
 *    cookie. Sign-out runs from the dashboard before navigating away, so
 *    the paths that matter are covered; keep it that way.
 *  - The Firebase Auth SDK keeps its own session in IndexedDB
 *    (`firebaseLocalStorageDb`), which this does not touch. Ending that
 *    needs `signOut()` from `firebase/auth` at the call site.
 */

/**
 * A cookie is identified by (name, domain, path) — deleting one means
 * writing an expired cookie whose three parts match exactly. We can read a
 * cookie's name but never its domain or path, so we expire each name across
 * every domain and path it could plausibly have been scoped to.
 */
function candidateDomains(): (string | undefined)[] {
    const host = window.location.hostname;

    // `undefined` covers the host-only cookie (set with no domain attribute),
    // which is what every cookie this app writes actually is.
    const domains: (string | undefined)[] = [ undefined ];

    // A bare hostname ("localhost") or an IP has no parent domains to walk.
    if (!host.includes(".") || /^[\d.]+$/.test(host)) {
        domains.push(host);
        return domains;
    }

    // www.unilinkd.com → "www.unilinkd.com", then "unilinkd.com". Stops one
    // label short of the end so we never try to write against the public
    // suffix ("com"), which browsers reject anyway.
    const labels = host.split(".");
    for (let i = 0; i < labels.length - 1; i++) {
        const domain = labels.slice(i).join(".");
        domains.push(domain, `.${domain}`);
    }

    return domains;
}

/** "/" plus every prefix of the current path, e.g. /dashboard, /dashboard/users. */
function candidatePaths(): string[] {
    const paths = new Set<string>([ "/" ]);

    let prefix = "";
    for (const segment of window.location.pathname.split("/").filter(Boolean)) {
        prefix += `/${segment}`;
        paths.add(prefix);
    }

    return [ ...paths ];
}

function expireCookie(name: string, path: string, domain?: string): void {
    const parts = [
        `${name}=`,
        "expires=Thu, 01 Jan 1970 00:00:01 GMT",
        `path=${path}`,
    ];
    if (domain) parts.push(`domain=${domain}`);

    document.cookie = parts.join("; ");
}

/**
 * Removes every cookie readable on the current origin.
 *
 * Names come from `document.cookie` raw rather than js-cookie's decoded
 * view, so cookies written by anything other than js-cookie (a third-party
 * script, a non-HttpOnly server cookie) are caught too.
 */
export function clearAllSiteCookies(): void {
    const names = document.cookie
        .split(";")
        .map(pair => pair.split("=")[ 0 ].trim())
        .filter(Boolean);

    if (names.length === 0) return;

    const domains = candidateDomains();
    const paths = candidatePaths();

    for (const name of names) {
        for (const domain of domains) {
            for (const path of paths) {
                expireCookie(name, path, domain);
            }
        }
    }
}

/**
 * Everything a sign-out should drop on the client: all cookies, plus both
 * web storages. Call `stopAuthRefresh()` before this — otherwise a refresh
 * tick can land mid-wipe and write `authToken` straight back.
 */
export function clearClientSession(): void {
    clearAllSiteCookies();
    localStorage.clear();
    sessionStorage.clear();
}
